import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUsers,
  getApartments,
  getOrganization,
  getOrganizationBySlug,
  getDashboardData,
  getIssues,
  getMaintenancePageData,
  getDocumentsPageData,
  getAnnouncements,
  queryCollection,
  getCommHubUsers,
  createCommHubUser,
  updateCommHubUser,
  deleteCommHubUser,
  getApartmentsPublic,
  getUpcomingMaintenance,
  getProperties,
  getSuppliers,
  getOrders,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  createIssue,
  updateIssue,
  deleteIssue,
  invalidateCommHubUsersCache,
  User,
  Apartment,
  Organization,
  Issue,
  Announcement,
  Property,
  Supplier,
  Order,
  MaintenanceSchedule,
} from '../lib/api';

// Query Keys - centraliserade för enkel invalidering
export const queryKeys = {
  users: (orgId?: string) => ['users', orgId] as const,
  commhubUsers: (orgId?: string) => ['commhub-users', orgId] as const,
  apartments: (orgId?: string) => ['apartments', orgId] as const,
  apartmentsPublic: (orgId: string) => ['apartments', 'public', orgId] as const,
  organization: (id: string) => ['organization', id] as const,
  organizationBySlug: (slug: string) => ['organization', 'slug', slug] as const,
  organizations: () => ['organizations'] as const,
  dashboard: (orgId?: string) => ['dashboard', orgId] as const,
  issues: (params?: Record<string, unknown>) => ['issues', params] as const,
  issue: (id: string) => ['issue', id] as const,
  maintenance: (orgId?: string) => ['maintenance', orgId] as const,
  upcomingMaintenance: (orgId?: string, daysAhead?: number) => ['maintenance', 'upcoming', orgId, daysAhead] as const,
  documents: (orgId?: string) => ['documents', orgId] as const,
  announcements: (orgId?: string) => ['announcements', orgId] as const,
  pendingUpdates: (orgId?: string) => ['pendingUpdates', orgId] as const,
  properties: (orgId?: string) => ['properties', orgId] as const,
  suppliers: (orgId?: string) => ['suppliers', orgId] as const,
  orders: (params?: Record<string, unknown>) => ['orders', params] as const,
  emergencyContacts: (orgId?: string) => ['emergencyContacts', orgId] as const,
  auditLog: (params?: Record<string, unknown>) => ['auditLog', params] as const,
};

// === USERS ===
export function useUsers(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.users(organizationId),
    queryFn: async () => {
      const response = await getUsers({ organization_id: organizationId });
      return response.data || [];
    },
    enabled: enabled && !!organizationId,
    staleTime: 2 * 60 * 1000, // 2 minuter
  });
}

export function useCommHubUsers(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: ['commhub-users', organizationId],
    queryFn: async () => {
      return await getCommHubUsers({ organization_id: organizationId, limit: 500 });
    },
    enabled: enabled && !!organizationId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createCommHubUser,
    onSuccess: () => {
      // Invalidera alla user-queries
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['commhub-users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateCommHubUser>[1] }) => 
      updateCommHubUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['commhub-users'] });
    },
  });
}

// === APARTMENTS ===
export function useApartments(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.apartments(organizationId),
    queryFn: async () => {
      const response = await getApartments({ organization_id: organizationId });
      return response.data || [];
    },
    enabled, // Allow fetching even without organizationId
    staleTime: 5 * 60 * 1000, // 5 minuter - lägenheter ändras sällan
  });
}

export function useApartmentsPublic(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.apartmentsPublic(organizationId),
    queryFn: async () => {
      const response = await getApartmentsPublic(organizationId);
      return response.data || [];
    },
    enabled: enabled && !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

// === ORGANIZATION ===
export function useOrganization(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.organization(id),
    queryFn: async () => {
      const response = await getOrganization(id);
      return response.data;
    },
    enabled: enabled && !!id,
    staleTime: 10 * 60 * 1000, // 10 minuter
  });
}

export function useOrganizationBySlug(slug: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.organizationBySlug(slug),
    queryFn: () => getOrganizationBySlug(slug),
    enabled: enabled && !!slug,
    staleTime: 10 * 60 * 1000,
  });
}

// === DASHBOARD ===
export function useDashboard(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.dashboard(organizationId),
    queryFn: () => getDashboardData(organizationId),
    enabled, // Allow fetching even without organizationId for admin users
    staleTime: 1 * 60 * 1000, // 1 minut - dashboard ska vara relativt färsk
  });
}

// === ISSUES ===
export function useIssues(params?: { organization_id?: string; status?: string; limit?: number }, enabled = true) {
  return useQuery({
    queryKey: queryKeys.issues(params),
    queryFn: async () => {
      const response = await getIssues(params);
      return response.data || [];
    },
    enabled,
    staleTime: 1 * 60 * 1000,
  });
}

// === MAINTENANCE ===
export function useMaintenance(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.maintenance(organizationId),
    queryFn: () => getMaintenancePageData(organizationId ?? null),
    enabled: enabled && !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

// === DOCUMENTS ===
export function useDocuments(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.documents(organizationId),
    queryFn: () => getDocumentsPageData(organizationId ?? null),
    enabled: enabled && !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

// === ANNOUNCEMENTS ===
export function useAnnouncements(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.announcements(organizationId),
    queryFn: () => getAnnouncements(organizationId!),
    enabled: enabled && !!organizationId,
    staleTime: 2 * 60 * 1000,
  });
}

// === PENDING MEMBER UPDATES ===
export function usePendingUpdates(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.pendingUpdates(organizationId),
    queryFn: async () => {
      const allUpdates = await queryCollection<{
        id: string;
        organization_id: string;
        apartment_id: string;
        residents: Array<{
          id: string;
          first_name?: string;
          last_name?: string;
          name?: string;
          email: string;
          phone: string;
          is_invoice_recipient?: boolean;
        }>;
        email_verified: boolean;
        completed?: boolean;
        created_at: string;
      }>('member_updates', { organization_id: organizationId }, { bypassCache: true });
      
      return allUpdates
        .filter(u => u.email_verified && !u.completed)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    },
    enabled: enabled && !!organizationId,
    staleTime: 30 * 1000, // 30 sekunder - pending updates ska vara färska
  });
}

// === PREFETCH HELPERS ===
export function usePrefetchDashboardData(organizationId?: string) {
  const queryClient = useQueryClient();
  
  return () => {
    if (!organizationId) return;
    
    // Prefetch alla data som behövs för dashboard
    queryClient.prefetchQuery({
      queryKey: queryKeys.dashboard(organizationId),
      queryFn: () => getDashboardData(organizationId),
    });
    
    queryClient.prefetchQuery({
      queryKey: queryKeys.issues({ organization_id: organizationId, limit: 5 }),
      queryFn: () => getIssues({ organization_id: organizationId, limit: 5 }),
    });
  };
}

// === UPCOMING MAINTENANCE ===
export function useUpcomingMaintenance(organizationId?: string, daysAhead = 30, enabled = true) {
  return useQuery({
    queryKey: queryKeys.upcomingMaintenance(organizationId, daysAhead),
    queryFn: async () => {
      const response = await getUpcomingMaintenance({ 
        organization_id: organizationId,
        days_ahead: daysAhead
      });
      return response.data || [];
    },
    enabled: enabled && !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

// === PROPERTIES ===
export function useProperties(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.properties(organizationId),
    queryFn: async () => {
      const response = await getProperties();
      const props = response.data || [];
      // Filter by org if provided
      if (organizationId) {
        return props.filter((p: Property) => p.organization_id === organizationId);
      }
      return props;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// === SUPPLIERS ===
export function useSuppliers(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.suppliers(organizationId),
    queryFn: async () => {
      const response = await getSuppliers();
      const suppliers = response.data || [];
      if (organizationId) {
        return suppliers.filter((s: Supplier) => s.organization_id === organizationId);
      }
      return suppliers;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// === ORDERS ===
export function useOrders(params?: { status?: string; issue_id?: string; organization_id?: string }, enabled = true) {
  return useQuery({
    queryKey: queryKeys.orders(params),
    queryFn: async () => {
      const response = await getOrders(params);
      return response.data || [];
    },
    enabled,
    staleTime: 1 * 60 * 1000,
  });
}

// === ORGANIZATIONS LIST ===
export function useOrganizations(enabled = true) {
  return useQuery({
    queryKey: queryKeys.organizations(),
    queryFn: async () => {
      const orgs = await queryCollection<Organization>('organizations');
      return orgs;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

// === EMERGENCY CONTACTS ===
export function useEmergencyContacts(organizationId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.emergencyContacts(organizationId),
    queryFn: async () => {
      const filter: Record<string, unknown> = {};
      if (organizationId) filter.organization_id = organizationId;
      return await queryCollection('emergency_contacts', filter);
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

// === AUDIT LOG ===
export function useAuditLog(params?: { organization_id?: string; limit?: number }, enabled = true) {
  return useQuery({
    queryKey: queryKeys.auditLog(params),
    queryFn: async () => {
      const filter: Record<string, unknown> = {};
      if (params?.organization_id) filter.organization_id = params.organization_id;
      return await queryCollection('audit_log', filter, { limit: params?.limit || 100 });
    },
    enabled,
    staleTime: 1 * 60 * 1000,
  });
}

// === ANNOUNCEMENT MUTATIONS ===
export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Announcement> }) => 
      updateAnnouncement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

// === ISSUE MUTATIONS ===
export function useCreateIssue() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: Parameters<typeof createIssue>[0]) => createIssue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateIssue() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data, options }: { id: string; data: Partial<Issue>; options?: Parameters<typeof updateIssue>[2] }) => 
      updateIssue(id, data, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['issue'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteIssue() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteIssue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// === USER MUTATIONS ===
export function useDeleteUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteCommHubUser,
    onSuccess: () => {
      invalidateCommHubUsersCache();
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['commhub-users'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// === INVALIDATION HELPERS ===
export function useInvalidateQueries() {
  const queryClient = useQueryClient();
  
  return {
    invalidateUsers: () => {
      invalidateCommHubUsersCache();
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['commhub-users'] });
    },
    invalidateApartments: () => {
      queryClient.invalidateQueries({ queryKey: ['apartments'] });
    },
    invalidateIssues: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    invalidateAnnouncements: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
    invalidateMaintenance: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    },
    invalidateAll: () => {
      queryClient.invalidateQueries();
    },
  };
}
